import { useEffect, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Modal } from '@shared/ui/Modal';
import { Button } from '@shared/ui/Button';
import { ACCEPT_CLAIM_DOC } from '@shared/lib/fileAccept';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { MultiFileList } from '@shared/ui/MultiFileList';
import { Table } from '@shared/ui/Table';
import { Loader } from '@shared/ui/Progress';
import {
  IconChevronDown,
  IconChevronRight,
  IconDownload,
  IconTrash,
} from '@shared/ui/icons';
import { downloadAuthorizedFile } from '@shared/lib/downloadFile';
import { useAppSelector } from '@app/hooks';
import {
  useDeleteClaimDocumentMutation,
  useListClaimDocumentsQuery,
  useUploadDocumentsToClaimMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';

/**
 * Блок «Документы по делу (N)» в карточке претензии/иска. У претензий нет
 * версионирования — все файлы попадают в один комплект `claims.documents`,
 * догрузка форсирует re-analyze без размножения записей. После успеха
 * показываем inline-баннер с количеством добавленных документов.
 */

const Section = styled.div`
  margin-top: ${({ theme }) => theme.spacing(5)};

  & > h4 {
    margin: 0 0 ${({ theme }) => theme.spacing(2)};
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  font-style: italic;
`;

const ToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: left;
  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
  & > h4 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: inherit;
  }
`;

const FileRow = styled.tr`
  cursor: pointer;
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const DownloadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;

const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.danger};
  transition: background ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.danger}14;
    border-color: ${({ theme }) => theme.colors.status.danger};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.status.danger};
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.success}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.success};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.success};
  font-size: 13px;
  line-height: 1.5;
`;

const ErrorBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(2.5)} ${({ theme }) => theme.spacing(3)};
  background: ${({ theme }) => theme.colors.status.danger}14;
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
  line-height: 1.5;
`;

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let idx = 0;
  let val = bytes;
  while (val >= 1024 && idx < units.length - 1) {
    val /= 1024;
    idx += 1;
  }
  const formatted = idx >= 2 ? val.toFixed(2) : Math.round(val).toString();
  return `${formatted.replace('.', ',')} ${units[idx]}`;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  claimId: string;
  /** В разборе ли документ — блокирует кнопку «Догрузить» чтобы не плодить
   *  повторных анализов поверх ещё не закончившегося. */
  uploadDisabled?: boolean;
}

export function ClaimDocumentsSection({ claimId, uploadDisabled }: Props) {
  const { can_write } = useAgentPermission('statut');
  const accessToken = useAppSelector((s) => s.auth.accessToken);
  const { data, isLoading } = useListClaimDocumentsQuery(claimId);
  const [uploadDocs, { isLoading: isUploading }] = useUploadDocumentsToClaimMutation();
  const [deleteDoc, { isLoading: isDeleting }] = useDeleteClaimDocumentMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [picked, setPicked] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Свёрнуто по умолчанию: юристу в первую очередь нужны риски и
  // позиция, список приложений (могут быть 20-50 файлов: переписка,
  // расчёты, копии) разворачивается по клику когда нужно скачать.
  const [expanded, setExpanded] = useState(false);
  // Один раз авто-разворачиваем при малом комплекте (≤5) — иначе юрист
  // не понимает где кнопка «Скачать» и думает что её нет вовсе.
  const [autoApplied, setAutoApplied] = useState(false);

  const items = data?.items ?? [];

  useEffect(() => {
    if (!autoApplied && items.length > 0 && items.length <= 5) {
      setExpanded(true);
      setAutoApplied(true);
    }
  }, [autoApplied, items.length]);

  async function onDownload(docId: string, filename: string) {
    const base = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
    const res = await downloadAuthorizedFile(
      `${base}/agents/statut/claims/${claimId}/documents/${docId}`,
      { accessToken, fallbackName: filename },
    );
    if (!res.ok) setError(`Не удалось скачать файл (${res.error}).`);
  }

  async function onDelete(docId: string, filename: string) {
    if (
      !window.confirm(`Удалить вложение «${filename}»? Действие необратимо.`)
    ) {
      return;
    }
    setError(null);
    try {
      await deleteDoc({ claimId, docId }).unwrap();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось удалить файл. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  function onCloseModal() {
    setModalOpen(false);
    setPicked([]);
    setError(null);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (picked.length === 0) return;
    setError(null);
    const form = new FormData();
    for (const f of picked) {
      form.append('file', f);
    }
    try {
      const res = await uploadDocs({ claimId, form }).unwrap();
      setSuccess(`Добавлено ${res.added} документ(ов), запущен анализ.`);
      onCloseModal();
    } catch (err) {
      const msg =
        (err as { data?: { error?: { message?: string } } })?.data?.error?.message ??
        (err as { error?: string })?.error ??
        'Не удалось загрузить документы. Попробуйте ещё раз.';
      setError(msg);
    }
  }

  return (
    <Section>
      <Header>
        <ToggleBtn
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`claim-docs-${claimId}`}
          title={expanded ? 'Свернуть список файлов' : 'Развернуть список файлов'}
        >
          {expanded ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
          <h4>Документы по делу ({items.length})</h4>
        </ToggleBtn>
        <Button
          type="button"
          $size="sm"
          $variant="secondary"
          onClick={() => {
            setSuccess(null);
            setModalOpen(true);
          }}
          disabled={uploadDisabled || !can_write}
          title={
            !can_write
              ? 'Только чтение'
              : uploadDisabled
                ? 'Дождитесь окончания текущего анализа'
                : 'Догрузить расчёт / переписку / приложения'
          }
        >
          Догрузить документы
        </Button>
      </Header>

      {expanded &&
        (isLoading ? (
          <Loader />
        ) : items.length === 0 ? (
          <Empty>
            Файлы не привязаны (старый комплект до multi-file). Догрузка создаст
            первую запись и форсирует повторный анализ.
          </Empty>
        ) : (
          <div id={`claim-docs-${claimId}`}>
            {(() => {
              const empty = items.filter((f) => (f.content_chars ?? 0) === 0);
              if (empty.length > 0) {
                return (
                  <ErrorBanner role="alert" style={{ marginTop: 0, marginBottom: 12 }}>
                    ⚠ Не распарсилось: <strong>{empty.length}</strong> из {items.length}{' '}
                    файл(ов). LLM их <strong>не видела</strong> при анализе — выделены
                    желтым.
                  </ErrorBanner>
                );
              }
              const totalChars = items.reduce(
                (sum, f) => sum + (f.content_chars ?? 0),
                0,
              );
              return (
                <Empty style={{ color: 'var(--osnova-text-muted, #888)' }}>
                  ✓ Все {items.length} файл(ов) проанализированы (
                  {totalChars.toLocaleString('ru-RU')} символов суммарно).
                </Empty>
              );
            })()}
            <Table>
              <thead>
                <tr>
                  <th style={{ width: '40%' }}>Имя файла</th>
                  <th style={{ width: '12%' }}>Размер</th>
                  <th style={{ width: '16%' }}>Извлечено LLM</th>
                  <th style={{ width: '16%' }}>Загружен</th>
                  <th style={{ width: '16%', textAlign: 'right' }}>Файл</th>
                </tr>
              </thead>
              <tbody>
                {items.map((f) => {
                  const chars = f.content_chars ?? 0;
                  const isEmpty = chars === 0;
                  return (
                    <FileRow
                      key={f.id}
                      onClick={() => onDownload(f.id, f.filename)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onDownload(f.id, f.filename);
                        }
                      }}
                      title="Скачать файл"
                      style={
                        isEmpty
                          ? { background: 'rgba(255, 152, 0, 0.10)' }
                          : undefined
                      }
                    >
                      <td>{f.filename}</td>
                      <td>{humanSize(f.size_bytes)}</td>
                      <td
                        style={{
                          color: isEmpty ? 'var(--osnova-warning, #ff9800)' : undefined,
                          fontWeight: isEmpty ? 600 : 400,
                        }}
                        title={
                          isEmpty
                            ? 'Файл не распарсился. LLM его не видела при анализе.'
                            : `${chars.toLocaleString('ru-RU')} символов текста извлечено в анализ`
                        }
                      >
                        {isEmpty
                          ? '⚠ не извлечено'
                          : `${chars.toLocaleString('ru-RU')} симв.`}
                      </td>
                      <td>{formatDateTime(f.created_at)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <RowActions>
                          <DownloadBtn
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDownload(f.id, f.filename);
                            }}
                            aria-label={`Скачать «${f.filename}»`}
                            title={`Скачать «${f.filename}»`}
                          >
                            <IconDownload size={14} />
                            Скачать
                          </DownloadBtn>
                          {can_write && (
                            <DeleteBtn
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(f.id, f.filename);
                              }}
                              disabled={isDeleting}
                              aria-label={`Удалить «${f.filename}»`}
                              title={`Удалить «${f.filename}»`}
                            >
                              <IconTrash size={14} />
                              Удалить
                            </DeleteBtn>
                          )}
                        </RowActions>
                      </td>
                    </FileRow>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ))}

      {success && <SuccessBanner role="status">{success}</SuccessBanner>}

      <Modal
        open={modalOpen}
        title="Догрузить документы по делу"
        onClose={onCloseModal}
      >
        <form onSubmit={onSubmit}>
          <FileDropZone
            multiple
            accept={ACCEPT_CLAIM_DOC}
            hint="Расчёт неустойки, переписка, копии договоров — будет запущен повторный анализ"
            onFiles={(files) =>
              setPicked((prev) => {
                const seen = new Set(prev.map((f) => `${f.name}::${f.size}`));
                const next = [...prev];
                for (const f of files) {
                  const key = `${f.name}::${f.size}`;
                  if (!seen.has(key)) {
                    next.push(f);
                    seen.add(key);
                  }
                }
                return next;
              })
            }
          />
          <MultiFileList
            files={picked}
            onRemove={(idx) => setPicked((prev) => prev.filter((_, i) => i !== idx))}
          />

          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

          <div
            style={{
              marginTop: 20,
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
            }}
          >
            <Button type="button" $variant="ghost" onClick={onCloseModal}>
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={isUploading || picked.length === 0 || !can_write}
              title={!can_write ? 'Только чтение' : undefined}
            >
              {isUploading
                ? 'Загрузка…'
                : `Загрузить ${picked.length || ''} и переанализировать`}
            </Button>
          </div>
        </form>
      </Modal>
    </Section>
  );
}
